// Mancala engine (pure), Kalah rules: 6 pits a side with 4 seeds each, and a store at each end. On your turn you
// pick up every seed in one of your pits and sow them one at a time into the next pits counterclockwise, your own
// store included and the computer's store skipped. If the last seed lands in your store, you go again. If it lands in
// one of your own pits that was empty and the pit opposite holds seeds, you capture those seeds and the last one.
// When either side has no seeds left in its pits, the other side puts its remaining seeds in its store, and the
// fuller store wins. Spec: .wiki/wiki/topics/game-later-candidates.md §2 (Kalah capture).
//
// pits[0..5] are your pits from left to right, pits[6] your store (on the right); pits[7..12] the computer's pits,
// running right to left along the top, and pits[13] its store (on the left). Pit i faces pit 12 - i.

export type Side = "you" | "cpu"
export type Level = "easy" | "medium" | "hard"
export const LEVELS: Level[] = ["easy", "medium", "hard"]
export const SEEDS = 4
export const YOUR_STORE = 6, CPU_STORE = 13

export interface MancalaState {
  pits: number[] // 14
  turn: Side
  over: null | { winner: Side | "draw"; you: number; cpu: number }
  moves: number // your moves so far
}

/** What one move did, for the messages. */
export interface Sowing { from: number; last: number; again: boolean; captured: number }

export const newGame = (): MancalaState => ({ pits: Array.from({ length: 14 }, (_, p) => (p === YOUR_STORE || p === CPU_STORE ? 0 : SEEDS)), turn: "you", over: null, moves: 0 })

const own = (side: Side): number[] => (side === "you" ? [0, 1, 2, 3, 4, 5] : [7, 8, 9, 10, 11, 12])
const store = (side: Side): number => (side === "you" ? YOUR_STORE : CPU_STORE)
export const sideOf = (pit: number): Side | null => (pit >= 0 && pit <= 5 ? "you" : pit >= 7 && pit <= 12 ? "cpu" : null)

export function legalMoves(s: MancalaState, side: Side = s.turn): number[] {
  return s.over ? [] : own(side).filter((p) => s.pits[p]! > 0)
}

/** Sows pit `from` for the side to move. Returns the new state and what happened. */
export function sow(s: MancalaState, from: number): [MancalaState, Sowing] {
  const side = s.turn
  const pits = s.pits.slice()
  let seeds = pits[from]!
  pits[from] = 0
  let at = from
  while (seeds > 0) {
    at = (at + 1) % 14
    if (at === store(side === "you" ? "cpu" : "you")) continue
    pits[at]!++
    seeds--
  }
  let captured = 0
  if (sideOf(at) === side && pits[at] === 1 && pits[12 - at]! > 0) {
    captured = pits[12 - at]! + 1
    pits[store(side)]! += captured
    pits[at] = 0
    pits[12 - at] = 0
  }
  const again = at === store(side)
  const moves = s.moves + (side === "you" ? 1 : 0)
  const next: MancalaState = { pits, turn: again ? side : side === "you" ? "cpu" : "you", over: null, moves }
  // The game ends when either row is empty: the other side banks what's left in its pits.
  const yourRow = own("you").reduce((k, p) => k + pits[p]!, 0), cpuRow = own("cpu").reduce((k, p) => k + pits[p]!, 0)
  if (yourRow === 0 || cpuRow === 0) {
    for (const p of [...own("you"), ...own("cpu")]) { pits[store(sideOf(p)!)]! += pits[p]!; pits[p] = 0 }
    const you = pits[YOUR_STORE]!, cpu = pits[CPU_STORE]!
    next.over = { winner: you > cpu ? "you" : cpu > you ? "cpu" : "draw", you, cpu }
  }
  return [next, { from, last: at, again: again && !next.over, captured }]
}

export const play = (s: MancalaState, from: number): MancalaState => sow(s, from)[0]

// ---- the computer ------------------------------------------------------------------------------------------------

/** Score from the computer's side: the stores, plus a little for seeds still on its side of the board. */
export function evaluate(s: MancalaState): number {
  const row = (side: Side): number => own(side).reduce((k, p) => k + s.pits[p]!, 0)
  return (s.pits[CPU_STORE]! - s.pits[YOUR_STORE]!) * 4 + (row("cpu") - row("you"))
}

const WIN = 10_000
function search(s: MancalaState, depth: number, alpha: number, beta: number, deadline: number): number {
  if (s.over) return s.over.winner === "draw" ? 0 : (s.over.winner === "cpu" ? WIN : -WIN) + (s.over.cpu - s.over.you)
  if (depth <= 0 || Date.now() > deadline) return evaluate(s)
  const moves = legalMoves(s)
  if (s.turn === "cpu") {
    let best = -Infinity
    for (const m of moves) {
      best = Math.max(best, search(play(s, m), depth - 1, alpha, beta, deadline))
      alpha = Math.max(alpha, best)
      if (alpha >= beta) break
    }
    return best
  }
  let best = Infinity
  for (const m of moves) {
    best = Math.min(best, search(play(s, m), depth - 1, alpha, beta, deadline))
    beta = Math.min(beta, best)
    if (alpha >= beta) break
  }
  return best
}

const DEPTH: Record<Level, number> = { easy: 1, medium: 4, hard: 12 }

/** The computer's choice. Easy plays a random pit a third of the time and otherwise looks one move ahead. */
export function computerMove(s: MancalaState, level: Level, rand: () => number = Math.random, ms = 700): number {
  const moves = legalMoves(s)
  if (moves.length === 1) return moves[0]!
  if (level === "easy" && rand() < 1 / 3) return moves[Math.floor(rand() * moves.length)]!
  return best(s, DEPTH[level], s.turn === "cpu" ? 1 : -1, rand, ms)
}

function best(s: MancalaState, depth: number, sign: 1 | -1, rand: () => number, ms: number): number {
  const deadline = Date.now() + ms
  const moves = legalMoves(s)
  let top: number[] = moves.slice(0, 1)
  for (let d = 1; d <= depth; d++) {
    let bestV = -Infinity, bestMoves: number[] = []
    for (const m of moves) {
      const v = sign * search(play(s, m), d - 1, -Infinity, Infinity, deadline)
      if (v > bestV) { bestV = v; bestMoves = [m] } else if (v === bestV) bestMoves.push(m)
    }
    if (Date.now() > deadline && d > 1) break
    top = bestMoves
  }
  return top[Math.floor(rand() * top.length)]!
}

/** Hint: a good pit for you (Medium strength). */
export const hintMove = (s: MancalaState): number => best(s, DEPTH.medium, -1, () => 0, 400)

/** Names a pit for people, counting from the left as you look at the board: your pits along the bottom are 1 to 6,
 *  and so are the computer's along the top. */
export function pitName(p: number): string {
  if (p === YOUR_STORE) return "your store"
  if (p === CPU_STORE) return "the computer's store"
  return sideOf(p) === "you" ? `your pit ${p + 1}` : `the computer's pit ${13 - p}`
}
